import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone, Globe, MapPin, Send, Building2, Handshake } from 'lucide-react';

const Participate = ({ onOpenForumModal }) => (
  <div className="min-h-screen pt-16">
    {/* PAGE HERO */}
    <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="participate-hero">
      <div className="absolute inset-0 opacity-10" aria-hidden="true" />
      <div className="relative container-institutional">
        <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Participer</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Rejoignez URBANIK 2026</h1>
        <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Choisissez votre mode de participation et rejoignez le plus grand forum dédié à l'innovation du BTP.</p>
      </div>
    </section>

    {/* PARTICIPATION OPTIONS */}
    <section id="inscription" className="py-20 lg:py-28" data-testid="participate-options">
      <div className="container-institutional">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Visiteur */}
          <div className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            <div className="p-8">
              <div className="w-14 h-14 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center mb-6"><Send className="w-6 h-6 text-[#1E3A5F]" /></div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">S'inscrire au Forum</h3>
              <p className="text-sm text-[#6B7C93] mb-6 leading-relaxed">Accédez aux conférences, à l'exposition, aux ateliers et au networking B2B pendant 3 jours.</p>
              <ul className="space-y-2 mb-6">
                {['Accès aux conférences', 'Exposition et démos', 'Networking B2B', 'Badge personnalisé'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-[#4A5568]"><div className="w-1.5 h-1.5 bg-[#F5A623] rounded-full" />{item}</li>
                ))}
              </ul>
              <button onClick={onOpenForumModal} className="btn-primary w-full py-3" data-testid="register-visitor-btn">S'inscrire <ArrowRight className="w-4 h-4 ml-2" /></button>
            </div>
          </div>

          {/* Exposant */}
          <div className="bg-[#1E3A5F] rounded-lg overflow-hidden">
            <div className="p-8">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-6"><Building2 className="w-6 h-6 text-[#F5A623]" /></div>
              <h3 className="text-xl font-bold text-white mb-3">Devenir Exposant</h3>
              <p className="text-sm text-white/70 mb-6 leading-relaxed">Présentez vos innovations, réservez un stand et connectez-vous avec les décideurs du secteur.</p>
              <ul className="space-y-2 mb-6">
                {['Stand personnalisé', 'Visibilité maximale', 'Rencontres B2B', 'Magazine URBANIK'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white/70"><div className="w-1.5 h-1.5 bg-[#F5A623] rounded-full" />{item}</li>
                ))}
              </ul>
              <Link to="/exposition/devenir-exposant" className="btn-primary w-full py-3 justify-center" data-testid="become-exposant-btn">Devenir Exposant <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </div>
          </div>

          {/* Partenaire */}
          <div className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            <div className="p-8">
              <div className="w-14 h-14 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center mb-6"><Handshake className="w-6 h-6 text-[#1E3A5F]" /></div>
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">Devenir Partenaire</h3>
              <p className="text-sm text-[#6B7C93] mb-6 leading-relaxed">Associez votre image au forum de référence du BTP et bénéficiez d'une visibilité premium.</p>
              <ul className="space-y-2 mb-6">
                {['Logo sur supports', 'Visibilité institutionnelle', 'Networking exclusif', 'Dîner gala VIP'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-[#4A5568]"><div className="w-1.5 h-1.5 bg-[#F5A623] rounded-full" />{item}</li>
                ))}
              </ul>
              <Link to="/exposition/devenir-exposant" className="btn-outline w-full py-3 justify-center" data-testid="become-partner-btn">Voir les offres <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CONTACT */}
    <section id="contact" className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="contact-section">
      <div className="container-institutional">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Contact</p>
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Nous Contacter</h2>
            <div className="w-16 h-1 bg-[#F5A623] rounded-full mb-8" />
            <p className="text-[#6B7C93] mb-8 leading-relaxed">Pour toute question sur la participation, les offres de partenariat ou les réservations de stands, contactez notre équipe.</p>
          </div>
          <div className="space-y-5">
            {[
              { icon: Mail, label: 'Email', value: 'urbanikforum@gmail.com', href: 'mailto:urbanikforum@gmail.com' },
              { icon: Phone, label: 'Téléphone', value: '+225 27 20 37 01 39 / 05 05 81 18 77' },
              { icon: Phone, label: 'Service commercial', value: '+225 07 88 73 20 28 / 01 52 81 41 36' },
              { icon: Globe, label: 'Site internet', value: 'www.urbanik.net', href: 'https://www.urbanik.net' },
              { icon: MapPin, label: 'Lieu', value: 'Palais des Congrès du Sofitel Hôtel Ivoire, Abidjan' },
            ].map((item) => (
              <div key={item.label + item.value} className="flex items-start gap-4 p-5 bg-white rounded-lg border border-[#E1E8F0]">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A5F]/5 flex items-center justify-center flex-shrink-0"><item.icon className="w-5 h-5 text-[#1E3A5F]" /></div>
                <div>
                  <p className="text-xs text-[#94A3B8] font-medium uppercase tracking-wider mb-1">{item.label}</p>
                  {item.href ? <a href={item.href} className="text-sm text-[#1E3A5F] font-medium hover:text-[#F5A623] transition-colors">{item.value}</a> : <p className="text-sm text-[#4A5568]">{item.value}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Participate;
